import type { ReactNode } from "react";

import { addDays, dayLabel, dayMonth, daysBetween, todayStr } from "../domain/date";
import { Tag } from "./Tag";

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

const AXIS_LEN = 358;

/**
 * A projected date with its spread (kit §26): the goal date, or any other estimate. `likely`,
 * `from` and `to` are `YYYY-MM-DD` date strings; the axis runs from today to `to` + 30 days (or
 * `likely` + 30 when `to` is missing) so the whole spread always has room. `likely: null` — under
 * 14 days of data — draws the figure as "–" and no bar; the not-yet-earned Insight belongs beside
 * it. The caller still owns "An estimate, not a promise." somewhere on the page.
 */
export function Range({
  label,
  likely,
  from,
  to,
  detail,
  ariaLabel,
}: {
  label: ReactNode;
  likely: string | null;
  from?: string;
  to?: string;
  detail?: string;
  ariaLabel: string;
}) {
  const today = todayStr();
  const axisEnd = to ? addDays(to, 30) : likely ? addDays(likely, 30) : addDays(today, 30);
  const totalDays = Math.max(1, daysBetween(today, axisEnd));
  const xFor = (ds: string) => (clamp(daysBetween(today, ds), 0, totalDays) / totalDays) * AXIS_LEN;

  return (
    <div className="k-range">
      <p className="k-range__label">{label}</p>
      <p className="k-range__head">
        <span className={`k-range__fig${likely === null ? " is-empty" : ""}`}>
          {likely === null ? "–" : `~${dayLabel(likely)}`}
        </span>
        <Tag tone="est" />
      </p>
      {detail !== undefined ? <p className="k-range__detail">{detail}</p> : null}
      {likely !== null ? (
        <>
          <svg
            className="k-range__bar"
            width="100%"
            height="16"
            viewBox={`0 0 ${AXIS_LEN} 16`}
            preserveAspectRatio="none"
            role="img"
            aria-label={ariaLabel}
          >
            <rect className="k-range__track" x="0" y="4" width={AXIS_LEN} height="8" rx="4" />
            {from && to ? (
              <rect
                className="k-range__fill"
                x={xFor(from)}
                y="4"
                width={Math.max(0, xFor(to) - xFor(from))}
                height="8"
                rx="4"
              />
            ) : null}
            <rect className="k-range__tick" x={xFor(likely) - 1} y="0" width="2" height="16" rx="1" />
          </svg>
          <div className="k-range__axis">
            <span>today</span>
            {from ? <span>{dayMonth(from)}</span> : null}
            {to ? <span>{dayMonth(to)}</span> : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
